import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";

export default function PackageDetailPage() {
    const { packageId } = useParams();
    const [pkg, setPkg] = useState(null);

    useEffect(() => {
        api.get(`/packages/${packageId}`).then((r) => setPkg(r.data));
    }, [packageId]);

    if (!pkg) {
        return (
            <div className="pt-40 pb-24 max-w-7xl mx-auto px-6 lg:px-10">
                <p className="text-forest-700/60">Loading…</p>
            </div>
        );
    }

    return (
        <div data-testid={`package-detail-${pkg.id}`} className="pt-28 lg:pt-32 pb-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <Link
                    to="/packages"
                    data-testid="back-to-packages"
                    className="inline-flex items-center gap-2 text-sm text-forest-700/70 hover:text-forest-700 mb-8"
                >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                    All packages
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-7">
                        <div className="aspect-[4/3] overflow-hidden rounded-sm bg-sand-200">
                            <img src={pkg.image_url} alt={pkg.name} className="w-full h-full object-cover" />
                        </div>
                    </div>

                    <div className="lg:col-span-5 flex flex-col">
                        <span className="overline">
                            {pkg.category === "drop_off" ? "Drop Off" : "On-Site Chef"}
                        </span>
                        <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl text-forest-700 mt-4 leading-[1.05]">
                            {pkg.name}
                        </h1>
                        <p className="text-forest-700/70 leading-relaxed mt-6">
                            {pkg.description}
                        </p>

                        <ul className="mt-8 space-y-3">
                            {pkg.features.map((f) => (
                                <li
                                    key={f}
                                    className="flex items-start gap-3 text-forest-700/85"
                                >
                                    <Check className="w-4 h-4 mt-1 text-forest-400 shrink-0" strokeWidth={1.5} />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-10 p-6 border border-palm-400/40 rounded-sm bg-white">
                            <div className="flex items-end justify-between">
                                <div>
                                    <span className="overline">From</span>
                                    <div className="font-serif text-5xl text-emerald-600 leading-none mt-2">
                                        {pkg.flat_price
                                            ? fmtUSD(pkg.flat_price)
                                            : fmtUSD(pkg.price_per_person)}
                                    </div>
                                    <div className="text-sm text-forest-700/60 mt-2">
                                        {pkg.flat_price ? pkg.serves : "per person"} ·{" "}
                                        min {pkg.min_guests} guests
                                    </div>
                                </div>
                            </div>
                            <Link
                                to={pkg.id === "easy-arrival" ? "/easy-arrival/choose-meal" : `/book/${pkg.id}`}
                                data-testid={`book-package-${pkg.id}`}
                                className="mt-6 block text-center bg-forest-700 text-sand-100 px-6 py-3.5 rounded-sm hover:bg-forest-900 transition-colors text-sm tracking-wide"
                            >
                                {pkg.id === "easy-arrival" ? "Choose your meal" : "Reserve this menu"}
                            </Link>
                            <p className="text-xs text-forest-700/60 mt-3 leading-relaxed">
                                Final total includes 20% gratuity, 5% processing fee,
                                {pkg.category === "drop_off"
                                    ? " and $65 delivery."
                                    : " and $150 server fee for 6+ guests."}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
